<script>
import { mapState } from 'vuex'
import stripeApi from '@/mixins/stripeApi'

import { PAYMENT_METHODS_QUERY } from '@/graph/gql/payment.gql'
import { addPaymentMethod } from '@/graph/mutations/payment'

const { VUE_APP_STRIPE_PUBLIC_KEY } = process.env

export default {
  name: 'AddPaymentMethodView',
  mixins: [stripeApi],
  apollo: {
    paymentMethods: PAYMENT_METHODS_QUERY
  },
  data: () => ({
    stripe: null,
    stripeKey: VUE_APP_STRIPE_PUBLIC_KEY,

    number: false,
    expiry: false,
    cvc: false,

    loading: false
  }),
  computed: {
    ...mapState({
      chef: state => state.cart.chef,
      cartItems: state => state.cart.items
    })
  },
  watch: {
    number() {
      this.update()
    },
    expiry() {
      this.update()
    },
    cvc() {
      this.update()
    }
  },
  mounted() {
    if (this.stripeApiLoaded && Stripe) {
      this.createElements()
      // Otherwise, set up a watcher so that when the library is loaded
      // we create our Element
    } else {
      this.$watch('stripeApiLoaded', this.createElements)
    }
  },
  updated() {
    if (!this.elements) this.createElements()
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },

    createElements() {
      this.$nextTick(() => {
        if (!Stripe) return

        this.stripe = Stripe(this.stripeKey)
        this.elements = this.stripe.elements({
          fonts: [{ cssSrc: 'https://rsms.me/inter/inter.css' }],
          locale: 'EN'
        })

        const style = {
          base: {
            color: 'rgba(0, 0, 0, 0.8)',
            fontFamily: `'Inter', sans-serif`,
            fontSize: '16px',
            fontWeight: '500',
            fontSmoothing: 'antialiased',
            iconColor: '#455a64',
            '::placeholder': {
              color: 'rgb(0,0,0,0.54)'
            }
          },
          invalid: {
            color: '#ff5252',
            iconColor: '#ff5252'
          }
        }

        this.cardNumber = this.elements.create('cardNumber', { style })
        this.cardNumber.mount('#card-number')
        this.cardNumber.addEventListener('change', ({ complete, error }) => {
          if (error) {
            // TODO: add error handling
          } else {
            this.number = complete
          }
        })

        this.cardExpiry = this.elements.create('cardExpiry', { style })
        this.cardExpiry.mount('#card-expiry')
        this.cardExpiry.addEventListener('change', ({ complete, error }) => {
          if (error) {
            // TODO: add error handling
          } else {
            this.expiry = complete
          }
        })

        this.cardCvc = this.elements.create('cardCvc', { style })
        this.cardCvc.mount('#card-cvc')
        this.cardCvc.addEventListener('change', ({ complete, error }) => {
          if (error) {
            // TODO: add error handling
          } else {
            this.cvc = complete
          }
        })
      })
    },

    update() {
      this.complete = this.number && this.expiry && this.cvc

      // field completed, find field to focus next
      if (this.number) {
        if (!this.expiry) {
          this.$refs.cardExpiry.focus()
        } else if (!this.cvc) {
          this.$refs.cardCvc.focus()
        }
      } else if (this.expiry) {
        if (!this.cvc) {
          this.$refs.cardCvc.focus()
        } else if (!this.number) {
          this.$refs.cardNumber.focus()
        }
      }
      // no focus magic for the CVC field as it gets complete with three
      // numbers, but can also have four
    },

    async addPaymentMethod() {
      if (this.loading || !this.number || !this.expiry || !this.cvc) return
      this.loading = true

      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumber,
        billing_details: {
          name: 'Test'
        }
      })

      if (error) {
        console.error(error)
        this.loading = false
        return this.$toast.error(error.message)
      }

      const { id, card } = paymentMethod

      const data = {
        pmId: id,
        brand: card.brand,
        country: card.country,
        expMonth: card.exp_month,
        expYear: card.exp_year,
        last4: card.last4,
        gateway: 'STRIPE',
        formattedGateway: 'Stripe'
      }

      // TODO: add error handling
      addPaymentMethod(data)
        .then(({ data }) => {
          const { chef, cartItems } = this
          if (chef && cartItems.length) {
            this.$store.commit('cart/setPaymentMethod', data.addPaymentMethod)
            this.$router.replace({ name: 'CheckoutView' })
          } else this.goBack()
        })
        .finally(() => (this.loading = false))
    }
  }
}
</script>

<template>
  <div>
    <!-- Toolbar -->
    <v-app-bar color="white" flat app>
      <v-btn icon @click="goBack">
        <v-icon color="black" size="18">$vuetify.icons.arrowBack</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Add Payment Method view page -->
    <div class="view-grid">
      <transition name="slide-up" appear>
        <div class="content-top px-5">
          <div class="display-1 font-weight-bold mb-3">
            Add card
          </div>

          <div class="subtitle-1 font-weight-medium pb-5 mb-5 primary--text">
            <v-icon size="16" class="primary--text">$vuetify.icons.lock</v-icon>
            &nbsp;Card details saved securely
          </div>

          <div v-if="stripeApiLoaded">
            <div class="v-text-field">
              <div class="v-input__control">
                <div class="v-input__slot">
                  <div class="v-text-field__slot">
                    <label
                      class="v-label v-label--active theme--light"
                      for="card-number"
                      >Card number</label
                    >
                    <div
                      id="card-number"
                      ref="cardNumber"
                      class="stripe-element card-number"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex">
              <div class="v-text-field pr-3">
                <div class="v-input__control">
                  <div class="v-input__slot">
                    <div class="v-text-field__slot">
                      <label
                        class="v-label v-label--active theme--light"
                        for="card-expiry"
                        >Expiration</label
                      >
                      <div
                        id="card-expiry"
                        ref="cardExpiry"
                        class="stripe-element card-expiry"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="v-text-field pl-3">
                <div class="v-input__control">
                  <div class="v-input__slot">
                    <div class="v-text-field__slot">
                      <label
                        class="v-label v-label--active theme--light"
                        for="card-cvc"
                        >Security code</label
                      >
                      <div
                        id="card-cvc"
                        ref="cardCvc"
                        class="stripe-element card-cvc"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <v-btn
              :disabled="!number || !expiry || !cvc"
              :loading="loading"
              rounded
              depressed
              color="primary"
              class="mt-6 px-6 mb-2"
              @click="addPaymentMethod"
            >
              Add card
            </v-btn>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.content-top
  margin-top: 56px

.d-flex .v-text-field
  width: 50%

.v-label
  position: absolute

.stripe-element
  width: 100%
  padding: 8px 0
</style>
